// Versioned internal events (PRD §16). Status is derived from these facts, never from
// free text produced by an agent.
import { z } from "zod";

/** Event types named by the PRD, plus product events needed by the foundation. */
export const EVENT_TYPES = [
  // PRD §16
  "task.created",
  "task.ready",
  "task.execution.requested",
  "run.started",
  "run.checkpointed",
  "task.blocked",
  "test.completed",
  "review.completed",
  "maestro.review.started",
  "maestro.review.completed",
  "maestro.review.inconclusive",
  "integration.confirmed",
  "task.completed",
  "goal.audit.started",
  "goal.audit.completed",
  "goal.blocked",
  "quota.changed",
  "quota.wait.started",
  "quota.wait.released",
  "quota.reservation.created",
  "quota.reservation.reconciled",
  "memory.evaluation.completed",
  "routing.config.activated",
  "agent.profile.activated",
  "connection.test.completed",
  "model.capability.probed",
  "model.backend.conformance.completed",
  "model.route.selected",
  "model.escalation.requested",
  "model.escalation.started",
  "model.call.started",
  "model.call.finished",
  "model.call.interrupted",
  "tool.call.requested",
  "tool.call.settled",
  "context.compacted",
  "context.compaction.rejected",
  "gauntlet.round.started",
  "gauntlet.round.reviewed",
  "gauntlet.stalled",
  "gauntlet.budget.exhausted",
  "runtime.started",
  "runtime.draining",
  "runtime.recovered",
  "runtime.crash_loop",
  "process.started",
  "process.finished",
  "process.failed",
  "telemetry.gap.detected",
  "health.changed",
  "alert.opened",
  "alert.recovered",
  // Foundation events (product)
  "project.created",
  "project.updated",
  "project.deleted",
  "project.execution.changed",
  "project.validation.completed",
  "task.transitioned",
  "task.updated",
  "task.override.recorded",
  /** Audit of the placement chosen on task creation (prioritized / start_now) and its outcome. */
  "task.placement.applied",
  "prd.imported",
  "prd.version.added",
  "prd.impact.analyzed",
  "requirement.extracted",
  "secret.stored",
  "secret.revoked",
  "policy.changed",
  "approval.recorded",
  "inbox.received",
  "external_operation.recorded",
  "external_operation.reconciled",
  // Wave 2 control-plane events
  "maestro.review.requested",
  "context.compaction.requested",
  /** Audit of provider connection / model target / agent profile / quota pool / Maestro changes. */
  "config.changed",
  // Wave 3 (council D11 item 6)
  "webhook.received",
  "inbox.processed",
  "inbox.dead_lettered",
  "incident.grouped",
  "pull_request.head_changed",
  "browser.run.completed",
  "preview.changed",
  "memory.index.activated",
  // Routing Intelligence v2 (D24, PRD §34)
  "routing.classification.started",
  "routing.classification.completed",
  "routing.classification.degraded",
  "routing.decision.created",
  "routing.route.pinned",
  "routing.cache.cold",
  "routing.cache.warm",
  "routing.cache.lost",
  "routing.provider.changed",
  "routing.escalation.requested",
  "routing.escalation.approved",
  "routing.escalation.started",
  "routing.model.degraded",
  "routing.model.recovered",
  // Assistant panel (spec 2026-09-28)
  "assistant.turn.started",
  "assistant.reply.ready",
  "assistant.turn.progress",
  "assistant.action.executed",
  // Existing-project onboarding (spec 2026-09-28)
  "codebase_analysis.started",
  "codebase_analysis.finished",
  "codebase_analysis.attested",
  "codebase_analysis.attestation_revoked",
  "repository.provision.changed",
  // Micro-system publish (D28)
  "project.publish.changed",
  // Content language (D37.3): the "Translate tasks" runs of a project
  "task.translation.requested",
  "task.translation.completed",
  // MFA regeneration and operator recovery (D30); payloads never carry a secret
  "auth.mfa.regenerated",
  "auth.mfa.reset_by_operator",
  // D37: a person decided on a blocked task (retry, waive the tests, release dependents…); a block reclassified
  "task.block_decision.applied",
  "task.block.reclassified",
  "gate.waived",
  // D41-C3: the watchdog's act mode (a recovery requested and resumed) and a block released on server-observed facts
  "watchdog.recovery.requested",
  "watchdog.recovery.resumed",
  "task.block.auto_released",
  // D41-C12: an evaluation of an agent profile (recorded from a collection, never the executor's own judgement) and
  // a proposed change of a profile (a candidate version awaiting an evaluation; it never moves the current version)
  "agent.evaluation.recorded",
  "agent.change.proposed",
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export const ACTOR_TYPES = ["user", "agent", "service", "system", "integration"] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export const eventEnvelopeSchema = z.object({
  /** Opaque, commit-order position used by SSE (`Last-Event-ID`) and history (migration 0002). */
  cursor: z.string(),
  event_id: z.uuid(),
  schema_version: z.number().int().positive(),
  type: z.enum(EVENT_TYPES),
  organization_id: z.uuid(),
  project_id: z.uuid().nullable(),
  aggregate_type: z.string(),
  aggregate_id: z.string(),
  aggregate_version: z.number().int().nonnegative(),
  actor_type: z.enum(ACTOR_TYPES),
  actor_id: z.string().nullable(),
  correlation_id: z.string(),
  occurred_at: z.iso.datetime(),
  payload: z.record(z.string(), z.unknown()),
});
export type EventEnvelope = z.infer<typeof eventEnvelopeSchema>;
